import axios from "axios";

const ensureSlash = (url) => (url?.endsWith("/") ? url : `${url}/`);

const AUTH_BASE   = ensureSlash(process.env.REACT_APP_AUTH_BASE || "");
const GOOGLE_BASE = ensureSlash(process.env.REACT_APP_GOOGLE_SCRAPER_BASE || "");
const APPLE_BASE  = ensureSlash(process.env.REACT_APP_APPLE_SCRAPER_BASE  || "");

if (!AUTH_BASE)   console.warn("REACT_APP_AUTH_BASE is not set");
if (!GOOGLE_BASE) console.warn("REACT_APP_GOOGLE_SCRAPER_BASE is not set");
if (!APPLE_BASE)  console.warn("REACT_APP_APPLE_SCRAPER_BASE is not set");

// --- token helpers ---
export const getAccessToken  = () => localStorage.getItem("accessToken");
export const getRefreshToken = () => localStorage.getItem("refreshToken");
export const setTokens = ({ access, refresh }) => {
  if (access)  localStorage.setItem("accessToken", access);
  if (refresh) localStorage.setItem("refreshToken", refresh);
};
export const clearTokens = () => {
  localStorage.removeItem("accessToken");
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("userEmail");
};

export const authApi = axios.create({
  baseURL: AUTH_BASE,
  headers: { "Content-Type": "application/json" },
  timeout: 30000,
});

export const googleApi = axios.create({
  baseURL: GOOGLE_BASE,
  headers: { "Content-Type": "application/json" },
  timeout: 180000,
});

export const appleApi = axios.create({
  baseURL: APPLE_BASE,
  headers: { "Content-Type": "application/json" },
  timeout: 30000,
});

// Attach access token to every request (if present)
const attachAuthHeader = (config) => {
  const token = getAccessToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
};
authApi.interceptors.request.use(attachAuthHeader);
googleApi.interceptors.request.use(attachAuthHeader);
appleApi.interceptors.request.use(attachAuthHeader);

// --- Refresh handling (single-flight queue) ---
let isRefreshing = false;
let refreshQueue = [];

const processQueue = (error, newToken = null) => {
  refreshQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(newToken);
  });
  refreshQueue = [];
};

const refreshTokens = async () => {
  const refresh = getRefreshToken();
  if (!refresh) throw new Error("No refresh token");

  const res = await authApi.post("token/refresh/", { refresh });
  const { access } = res.data || {};
  if (!access) throw new Error("No access token in refresh response");

  setTokens({ access }); // keep existing refresh
  return access;
};

// Response interceptors (401 -> try refresh once)
const create401Interceptor = (client) =>
  client.interceptors.response.use(
    (res) => res,
    async (error) => {
      const originalRequest = error.config;

      // If request already retried or it's a refresh call itself -> reject
      const isAuthRefresh = originalRequest?.url?.includes("token/refresh/");
      if (error?.response?.status !== 401 || originalRequest.__isRetryRequest || isAuthRefresh) {
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // queue and wait for ongoing refresh
        return new Promise((resolve, reject) => {
          refreshQueue.push({
            resolve: (newToken) => {
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              originalRequest.__isRetryRequest = true;
              resolve(client(originalRequest));
            },
            reject,
          });
        });
      }

      originalRequest.__isRetryRequest = true;
      isRefreshing = true;

      try {
        const newAccess = await refreshTokens();
        processQueue(null, newAccess);

        // set header and retry the original request
        originalRequest.headers.Authorization = `Bearer ${newAccess}`;
        return client(originalRequest);
      } catch (refreshErr) {
        processQueue(refreshErr, null);
        clearTokens();
        // Optionally redirect to login:
        window.location.assign('/login');
        return Promise.reject(refreshErr);
      } finally {
        isRefreshing = false;
      }
    }
  );

// install on all clients that hit protected routes
create401Interceptor(authApi);
create401Interceptor(googleApi);
create401Interceptor(appleApi);

// --- Convenience auth calls (optional)
export const Auth = {
  register: (payload) => authApi.post("register/", payload),
  login:    (payload) => authApi.post("login/", payload),
  logout:   (refresh) => authApi.post("logout/", { refresh }),
  verifyEmail: (params) => authApi.get("verify-email/", { params }), // { uid, token }
  forgot:   (payload) => authApi.post("forgot-password/", payload),
  reset:    (payload) => authApi.post("reset-password/", payload),
};